/**
 * Easings - TSL easing functions for VFX.
 */

import { cos, float, int, PI, pow, select, sin, sqrt } from 'three/tsl';

import type { Node } from 'three/webgpu';

type FloatNode = Node<'float'>;
type IntNode = Node<'int'>;

/** Applies easing by ID using TSL nodes. */
export function applyEasing(t: FloatNode, easingId: IntNode): FloatNode {
  const one = float(1.0);
  const two = float(2.0);
  const half = float(0.5);

  const easeOutBounce = (x: FloatNode): FloatNode => {
    const c1 = float(1.0 / 2.75);
    const c2 = float(2.0 / 2.75);
    const c3 = float(2.5 / 2.75);
    const d1 = x.sub(1.5 / 2.75);
    const d2 = x.sub(2.25 / 2.75);
    const d3 = x.sub(2.625 / 2.75);

    return select(
      x.lessThan(c1),
      float(7.5625).mul(x).mul(x),
      select(
        x.lessThan(c2),
        float(7.5625).mul(d1).mul(d1).add(0.75),
        select(
          x.lessThan(c3),
          float(7.5625).mul(d2).mul(d2).add(0.9375),
          float(7.5625).mul(d3).mul(d3).add(0.984375)
        )
      )
    ) as FloatNode;
  };

  const easeInOutBack = (x: FloatNode): FloatNode => {
    const s = float(1.70158 * 1.525);
    const x2 = x.mul(two);
    return select(
      x2.lessThan(one),
      half.mul(x2.mul(x2).mul(s.add(1.0).mul(x2).sub(s))),
      half.mul(x2.sub(two).mul(x2.sub(two)).mul(s.add(1.0).mul(x2.sub(two)).add(s)).add(two))
    ) as FloatNode;
  };

  const easingCases: Array<(x: FloatNode) => FloatNode> = [
    (x) => x, // easeLinear
    (x) => x, // easeInPower1
    (x) => one.sub(one.sub(x)), // easeOutPower1
    (x) => x, // easeInOutPower1
    (x) => x.mul(x), // easeInPower2
    (x) => one.sub(pow(one.sub(x), 2.0) as FloatNode), // easeOutPower2
    (x) =>
      select(
        x.lessThan(0.5),
        float(2.0).mul(x).mul(x),
        one.sub((pow(float(-2.0).mul(x).add(2.0), 2.0) as FloatNode).div(two))
      ) as FloatNode, // easeInOutPower2
    (x) => x.mul(x).mul(x), // easeInPower3
    (x) => one.sub(pow(one.sub(x), 3.0) as FloatNode), // easeOutPower3
    (x) =>
      select(
        x.lessThan(0.5),
        float(4.0).mul(x).mul(x).mul(x),
        one.sub((pow(float(-2.0).mul(x).add(2.0), 3.0) as FloatNode).div(two))
      ) as FloatNode, // easeInOutPower3
    (x) => x.mul(x).mul(x).mul(x), // easeInPower4
    (x) => one.sub(pow(one.sub(x), 4.0) as FloatNode), // easeOutPower4
    (x) =>
      select(
        x.lessThan(0.5),
        float(8.0).mul(x).mul(x).mul(x).mul(x),
        one.sub((pow(float(-2.0).mul(x).add(2.0), 4.0) as FloatNode).div(two))
      ) as FloatNode, // easeInOutPower4
    (x) => x.mul(x), // easeInQuad
    (x) => x.mul(float(2.0).sub(x)), // easeOutQuad
    (x) => select(x.lessThan(0.5), float(2.0).mul(x).mul(x), float(-1.0).add(float(4.0).sub(float(2.0).mul(x)).mul(x))) as FloatNode, // easeInOutQuad
    (x) => x.mul(x).mul(x), // easeInCubic
    (x) => one.add(x.sub(one).mul(x.sub(one)).mul(x.sub(one))), // easeOutCubic
    (x) =>
      select(
        x.lessThan(0.5),
        float(4.0).mul(x).mul(x).mul(x),
        x.sub(one).mul(float(2.0).mul(x).sub(2.0)).mul(float(2.0).mul(x).sub(2.0)).add(one)
      ) as FloatNode, // easeInOutCubic
    (x) => x.mul(x).mul(x).mul(x), // easeInQuart
    (x) => one.sub(x.sub(one).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one))), // easeOutQuart
    (x) => select(x.lessThan(0.5), float(8.0).mul(x).mul(x).mul(x).mul(x), one.sub(float(8.0).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one)))) as FloatNode, // easeInOutQuart
    (x) => x.mul(x).mul(x).mul(x).mul(x), // easeInQuint
    (x) => one.add(x.sub(one).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one))), // easeOutQuint
    (x) =>
      select(
        x.lessThan(0.5),
        float(16.0).mul(x).mul(x).mul(x).mul(x).mul(x),
        one.add(float(16.0).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one)).mul(x.sub(one)))
      ) as FloatNode, // easeInOutQuint
    (x) => float(-1.0).mul(cos(x.mul(PI).mul(0.5))).add(one), // easeInSine
    (x) => sin(x.mul(PI).mul(0.5)) as FloatNode, // easeOutSine
    (x) => float(-0.5).mul(cos(PI.mul(x)).sub(one)), // easeInOutSine
    (x) => select(x.equal(0.0), float(0.0), pow(2.0, float(10.0).mul(x.sub(one))) as FloatNode) as FloatNode, // easeInExpo
    (x) => select(x.equal(1.0), one, one.sub(pow(2.0, float(-10.0).mul(x)) as FloatNode)) as FloatNode, // easeOutExpo
    (x) =>
      select(
        x.equal(0.0).or(x.equal(1.0)),
        x,
        select(
          x.lessThan(0.5),
          half.mul(pow(2.0, float(20.0).mul(x).sub(10.0)) as FloatNode),
          half.mul(float(-1.0).mul(pow(2.0, float(-20.0).mul(x).add(10.0)) as FloatNode).add(two))
        )
      ) as FloatNode, // easeInOutExpo
    (x) => float(-1.0).mul(sqrt(one.sub(x.mul(x)) as FloatNode).sub(one)), // easeInCirc
    (x) => sqrt(one.sub(x.sub(one).mul(x.sub(one))) as FloatNode), // easeOutCirc
    (x) =>
      select(
        x.lessThan(0.5),
        float(-0.5).mul(sqrt(one.sub(two.mul(x).mul(two.mul(x))) as FloatNode).sub(one)),
        half.mul(sqrt(one.sub(two.mul(x).sub(two).mul(two.mul(x).sub(two))) as FloatNode).add(one))
      ) as FloatNode, // easeInOutCirc
    (x) =>
      select(
        x.equal(0.0).or(x.equal(1.0)),
        x,
        float(-1.0)
          .mul(pow(2.0, float(10.0).mul(x.sub(one))) as FloatNode)
          .mul(sin(x.sub(1.075).mul(float(2.0).mul(PI).div(0.3)) as FloatNode) as FloatNode)
      ) as FloatNode, // easeInElastic
    (x) =>
      select(
        x.equal(0.0).or(x.equal(1.0)),
        x,
        (pow(2.0, float(-10.0).mul(x)) as FloatNode)
          .mul(sin(x.sub(0.075).mul(float(2.0).mul(PI).div(0.3)) as FloatNode) as FloatNode)
          .add(one)
      ) as FloatNode, // easeOutElastic
    (x) =>
      select(
        x.lessThan(0.5),
        float(-0.5)
          .mul(pow(2.0, float(20.0).mul(x).sub(10.0)) as FloatNode)
          .mul(sin(float(20.0).mul(x).sub(11.125).mul(float(2.0).mul(PI).div(4.5)) as FloatNode) as FloatNode),
        (pow(2.0, float(-20.0).mul(x).add(10.0)) as FloatNode)
          .mul(sin(float(20.0).mul(x).sub(11.125).mul(float(2.0).mul(PI).div(4.5)) as FloatNode) as FloatNode)
          .mul(0.5)
          .add(one)
      ) as FloatNode, // easeInOutElastic
    (x) => {
      const s = float(1.70158);
      return x.mul(x).mul(s.add(one).mul(x).sub(s));
    }, // easeInBack
    (x) => {
      const s = float(1.70158);
      const xm1 = x.sub(one);
      return xm1.mul(xm1).mul(s.add(one).mul(xm1).add(s)).add(one);
    }, // easeOutBack
    (x) => easeInOutBack(x), // easeInOutBack
    (x) => one.sub(easeOutBounce(one.sub(x))), // easeInBounce
    (x) => easeOutBounce(x), // easeOutBounce
    (x) => select(x.lessThan(0.5), one.sub(easeOutBounce(one.sub(two.mul(x)))).mul(0.5), one.add(easeOutBounce(two.mul(x).sub(one))).mul(0.5)) as FloatNode, // easeInOutBounce
  ];

  let result: FloatNode = t;
  for (let i = 0; i < easingCases.length; i++) {
    result = select(easingId.equal(int(i)), easingCases[i](t), result) as FloatNode;
  }

  return result;
}

export const applyEasingTSL = applyEasing;
